/**
 * Validates a list of waypoints for a mission to ensure they meet the minimum requirements
 * for standard drone autopilots.
 * 
 * @param {Array} waypoints - The list of waypoints to validate
 * @returns {Object} - { valid: boolean, message: string | null }
 */
export const validateWaypoints = (waypoints) => {
    if (!waypoints || !Array.isArray(waypoints) || waypoints.length === 0) {
      return { valid: true, message: null }; // Empty waypoints are allowed
    }
  
    // Commands that don't require location coordinates (MAV_FRAME_MISSION commands)
    const NON_LOCATION_TYPES = [
      'RTL', 'CHANGE_SPEED', 'SET_HOME', 'DELAY', 'GUIDED_ENABLE',
      'CONDITION_DELAY', 'CONDITION_YAW', 'CONDITION_DISTANCE', 'CONDITION_CHANGE_ALT',
      'CAMERA_TRIGGER', 'IMAGE_START_CAPTURE', 'IMAGE_STOP_CAPTURE',
      'VIDEO_START_CAPTURE', 'VIDEO_STOP_CAPTURE', 'SET_CAMERA_ZOOM', 'SET_CAMERA_FOCUS',
      'DO_GIMBAL_MANAGER_PITCHYAW', 'DO_SET_SERVO', 'DO_REPEAT_SERVO',
      'DO_SET_RELAY', 'DO_REPEAT_RELAY', 'DO_GRIPPER', 'DO_AUX_FUNCTION',
      'DO_ENGINE_CONTROL', 'DO_GUIDED_LIMITS', 'DO_MOUNT_CONTROL', 'DO_JUMP', 'DO_WINCH',
      'SET_ROI_NONE', 'SCRIPT_TIME',
    ];
  
    for (let i = 0; i < waypoints.length; i++) {
      const wp = waypoints[i];
  
      if (!wp || typeof wp !== 'object') {
        return { valid: false, message: `Waypoint ${i + 1}: Invalid waypoint data` };
      }
  
      // Check if this is a location-based command
      const isLocationCommand = !NON_LOCATION_TYPES.includes(wp.type);
  
      // Only validate coordinates for location-based commands
      if (isLocationCommand) {
        if (wp.latitude === undefined || wp.latitude === null ||
          wp.longitude === undefined || wp.longitude === null) {
          return { valid: false, message: `Waypoint ${i + 1}: Missing coordinates` };
        }
  
        if (isNaN(wp.latitude) || wp.latitude < -90 || wp.latitude > 90) {
          return { valid: false, message: `Waypoint ${i + 1}: Invalid latitude (must be -90 to 90)` };
        }
  
        if (isNaN(wp.longitude) || wp.longitude < -180 || wp.longitude > 180) {
          return { valid: false, message: `Waypoint ${i + 1}: Invalid longitude (must be -180 to 180)` };
        }
  
        // Validate altitude for location commands
        if (wp.altitude === undefined || wp.altitude === null) {
          return { valid: false, message: `Waypoint ${i + 1}: Missing altitude` };
        }
  
        if (isNaN(wp.altitude) || wp.altitude < -1000 || wp.altitude > 50000) {
          return { valid: false, message: `Waypoint ${i + 1}: Invalid altitude (must be -1000 to 50000 meters)` };
        }
      }
    }
  
    // Warn about mission structure issues
    const typeCount = {};
    waypoints.forEach(wp => { typeCount[wp.type] = (typeCount[wp.type] || 0) + 1; });
  
    if ((typeCount['TAKEOFF'] || 0) > 1) {
      return { valid: false, message: 'Multiple TAKEOFF commands found. Only one TAKEOFF is allowed.' };
    }
    if ((typeCount['LAND'] || 0) > 1) {
      return { valid: false, message: 'Multiple LAND commands found. Only one LAND is allowed.' };
    }
    if ((typeCount['RTL'] || 0) > 1) {
      return { valid: false, message: 'Multiple RTL commands found. Only one RTL is allowed.' };
    }
  
    // Check TAKEOFF is first location command (if present)
    const locationWps = waypoints.filter(wp => !NON_LOCATION_TYPES.includes(wp.type));
    if (typeCount['TAKEOFF'] && locationWps.length > 0 && locationWps[0].type !== 'TAKEOFF') {
      return { valid: false, message: 'TAKEOFF must be the first command in the mission.' };
    }
  
    // Check LAND/RTL is last (if present) — use the full waypoints list, not locationWps
    if (typeCount['LAND'] || typeCount['RTL']) {
      const lastWp = waypoints[waypoints.length - 1];
      if (lastWp && lastWp.type !== 'LAND' && lastWp.type !== 'RTL') {
        return { valid: false, message: 'LAND or RTL should be the last command in the mission.' };
      }
    }
  
    return { valid: true, message: null };
  };
